import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store,
  CheckCircle,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import shopService, { StoreType } from '../../services/shop.service';

const StoreTypes: React.FC = () => {
  const { t } = useTranslation();
  const [types, setTypes] = useState<StoreType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<StoreType | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    status: 'active' as 'active' | 'inactive',
  });

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label={t('shop.active')} icon={<CheckCircle />} />
    ) : (
      <Chip size="small" color="default" label={t('shop.inactive')} icon={<InactiveIcon />} />
    );
  };

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shopService.getStoreTypes();

      if (response.success) {
        setTypes(response.data || []);
        if (response.needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching store types:', err);
      setError(t('shop.errorLoadingTypes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleOpenEdit = (type?: StoreType) => {
    if (type) {
      setSelectedType(type);
      setFormData({
        name: type.name,
        description: type.description || '',
        icon: type.icon || '',
        status: type.status,
      });
    } else {
      setSelectedType(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedType) {
        await shopService.updateStoreType(selectedType.id, formData);
        setSuccessMessage(t('shop.typeUpdated'));
      } else {
        await shopService.createStoreType(formData);
        setSuccessMessage(t('shop.typeCreated'));
      }
      setEditDialog(false);
      setSelectedType(null);
      fetchTypes();
    } catch (err: any) {
      console.error('Error saving store type:', err);
      setError(t('shop.errorSavingType'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: StoreType) => {
    if (type.count > 0) {
      setError(t('shop.cannotDeleteType', { count: type.count }));
      return;
    }
    if (window.confirm(t('shop.confirmDeleteType', { name: type.name }))) {
      try {
        await shopService.deleteStoreType(type.id);
        setSuccessMessage(t('shop.typeDeleted'));
        fetchTypes();
      } catch (err: any) {
        console.error('Error deleting store type:', err);
        setError(err.response?.data?.error || t('shop.errorDeletingType'));
      }
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await shopService.setupTables();
      if (response.success) {
        setSuccessMessage(response.message);
        setNeedsSetup(false);
        fetchTypes();
      }
    } catch (err: any) {
      console.error('Error setting up tables:', err);
      setError(t('shop.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  const totalStores = types.reduce((sum, t) => sum + t.count, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('shop.storeTypes')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTypes}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('shop.newType')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('shop.settingUp') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('shop.setupNeeded')}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Store color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {types.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.storeTypesCount')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {totalStores}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.totalStores')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.storeTypeHeader')}</TableCell>
                  <TableCell>{t('shop.descriptionHeader')}</TableCell>
                  <TableCell align="center">{t('shop.storesHeader')}</TableCell>
                  <TableCell>{t('shop.statusHeader')}</TableCell>
                  <TableCell align="center">{t('shop.actionsHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {types.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">{t('shop.noTypes')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  types.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{type.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {type.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={type.count} color="primary" />
                      </TableCell>
                      <TableCell>{getStatusChip(type.status)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenEdit(type)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(type)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Store />
          {selectedType ? t('shop.editType') : t('shop.newTypeTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.nameLabel')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('shop.descriptionLabel')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('shop.iconLabel')}
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                <MenuItem value="">{t('shop.iconNone')}</MenuItem>
                <MenuItem value="store">{t('shop.iconStore')}</MenuItem>
                <MenuItem value="food">{t('shop.iconFood')}</MenuItem>
                <MenuItem value="restaurant">{t('shop.iconRestaurant')}</MenuItem>
                <MenuItem value="coffee">{t('shop.iconCoffee')}</MenuItem>
                <MenuItem value="shopping_bag">{t('shop.iconFashion')}</MenuItem>
                <MenuItem value="smartphone">{t('shop.iconTech')}</MenuItem>
                <MenuItem value="briefcase">{t('shop.iconServices')}</MenuItem>
                <MenuItem value="heart">{t('shop.iconHealth')}</MenuItem>
                <MenuItem value="truck">{t('shop.iconDelivery')}</MenuItem>
                <MenuItem value="star">{t('shop.iconBeauty')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">{t('shop.active')}</MenuItem>
                <MenuItem value="inactive">{t('shop.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedType ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreTypes;
